import { useMemo } from "react";

import type { ImageProps } from "next/image";
import { getImageProps } from "next/image";

import type { HeroV2Props } from "@jti/ui/components/HeroV2";
import { breakpoints, useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint";

const resolveImageMetadataForBreakpoint = ({
  breakpoint,
  desktopImage,
  mediumImage,
  smallImage,
}: {
  breakpoint: keyof typeof breakpoints;
  desktopImage?: { alt?: string; title?: string };
  mediumImage?: { alt?: string; title?: string };
  smallImage?: { alt?: string; title?: string };
}) => {
  if (breakpoint === "mobile") {
    return {
      alt: smallImage?.alt || "",
      title: smallImage?.title,
    };
  }

  if (breakpoint === "tablet") {
    return {
      alt: mediumImage?.alt || smallImage?.alt || "",
      title: mediumImage?.title || smallImage?.title,
    };
  }

  return {
    alt: desktopImage?.alt || mediumImage?.alt || smallImage?.alt || "",
    title: desktopImage?.title || mediumImage?.title || smallImage?.title,
  };
};

export function ResponsiveBackgroundImage({ desktopImage, mediumImage, smallImage, index }: Props) {
  const { breakpoint } = useDeviceBreakpoint();

  const { alt = "", title } = useMemo(
    () =>
      resolveImageMetadataForBreakpoint({
        breakpoint,
        desktopImage,
        mediumImage,
        smallImage,
      }),
    [breakpoint, desktopImage, mediumImage, smallImage]
  );

  const common: Partial<ImageProps> = {
    fill: true,
    sizes: "100vw",
    priority: index === 0,
    style: { objectFit: "cover" },
    quality: 100,
  };

  const {
    props: { srcSet: desktop },
  } = getImageProps({
    ...common,
    src: desktopImage?.src || "",
    alt,
  });
  const {
    props: { srcSet: medium },
  } = getImageProps({
    ...common,
    src: mediumImage?.src || "",
    alt,
  });
  const {
    props: { srcSet: small, ...rest },
  } = getImageProps({
    ...common,
    src: smallImage?.src || "",
    alt,
  });

  return (
    <picture>
      <source media={breakpoints.desktop} srcSet={desktop} />
      <source media={breakpoints.tablet} srcSet={medium} />
      <source media={breakpoints.mobile} srcSet={small} />

      <img {...rest} alt={alt} title={title} />
    </picture>
  );
}

type Props = Parameters<NonNullable<ArrayElement<HeroV2Props["data"]>["renderBackgroundImage"]>>[0] & { index: number };
